import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { EMPLOYEE_CANDIDATE_SAL, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_GROUP, EMPLOYEE_INTERN_SAL, EMPLOYEESAL, SALARY_TEMPLATE } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { useLocation } from "react-router-dom";
import { Select, Table } from "antd";
import { salaryTypeOptions } from "../../../constants/enum";
import { cn } from "../../../utils/tailwindUtils";
import { Input } from "../../../components/ui/input";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';
import { canAccessEmployeeProfile } from "../../../utils/employeeProfileAccess";
import { EmployeeProfileViewOnlyBanner } from "../../../components/employee/EmployeeProfileAccessNotice";
import { isOwnEmployeeProfile } from "../../../utils/employeeProfile";
import {
    calculateSalaryBreakdown,
    clampGrossMonthly,
    parseOptionalNumber,
    round2,
    GROSS_MONTHLY_MAX,
} from "../../configuration/salaryTemplateCalc";

// Register the required components
ChartJS.register(ArcElement, Tooltip, Legend);


const regimeType = [
    { label: "Old Regime", value: 'Old Regime' },
    { label: "New Regime", value: 'New Regime' },
];

const init = {
    annual_ctc: 0,
    monthly_ctc: 0,
    salary_type: null,
    earnings: [],
    deductions: [],
    total_earning: {
        percentage: 0,
        monthly_amount: null,
        annual_amount: null
    },
    total_deduction: {
        percentage: 0,
        employee_monthly_amount: null,
        employer_monthly_amount: null,
        annual_amount: 0
    },
    net_salary: 0,
    gross_salary: 0,
    total_deductions_percentage: 0,
    total_deductions_monthly_amount: 0,
    total_deductions_annual_amount: 0,
    net_salary_monthly: 0,
    salary_id: 0,
    is_active: 0,
    salary_template_id: 0,
    gross_monthly_amount: 0,
    gross_annual_amount: 0,
    regime_type: "New Regime"
}

// Helper function to group data
const groupBy = (array, keyGetter) => {
    const map = new Map();
    array.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
};

const toLookupId = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return value.id ?? null;
    return value;
};

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const templateScopeIds = (template, key, fallback) => {
    const ids = Array.isArray(template?.[key]) ? template[key] : [];
    if (ids.length) return ids.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    const single = toLookupId(fallback);
    return single == null ? [] : [Number(single)];
};

const templateMatchesEmployee = (template, deptId, desigId) => {
    if (!template) return false;
    const deptIds = templateScopeIds(template, "department_ids", template.department);
    const desigIds = templateScopeIds(template, "designation_ids", template.designation);
    const deptOk = !deptIds.length || deptId == null || deptIds.includes(Number(deptId));
    const desigOk = !desigIds.length || desigId == null || desigIds.includes(Number(desigId));
    return deptOk && desigOk;
};

const templateGroupLabel = (template) => {
    const departments = (template?.departments || [])
        .map((row) => row?.department_name)
        .filter(Boolean);
    const designations = (template?.designations || [])
        .map((row) => row?.designation_name)
        .filter(Boolean);
    const departmentName = departments.join(", ")
        || template?.department_name
        || template?.department?.department_name
        || "N/A";
    const designationName = designations.join(", ")
        || template?.designation_name
        || template?.designation?.designation_name
        || "N/A";
    return `${departmentName} - ${designationName}`;
};

const formatGrossInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === "-" || value === "." || value === "-.") return value;
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return String(num);
};

const SalaryDetailsContent = forwardRef(({ employeeData = {}, employeeID = null, url = '' }, ref) => {

    // custom methods and dom helpers
    const { fetch, update } = useApiMethods()
    const location = useLocation()

    url = location.pathname.includes('/intern/onboarding/viewDetails')
        ? EMPLOYEE_INTERN_SAL
        : location.pathname.includes('/employee/onboarding/viewDetails')
            ? EMPLOYEE_CANDIDATE_SAL
            : location.pathname.includes('/employee/viewDetails')
                ? EMPLOYEESAL
                : (url || '')

    // prime variables
    const customConfig = JSON.parse(sessionStorage.getItem("customConfig")) || {};
    const is_super_admin = JSON.parse(sessionStorage.getItem("is_super_admin")) || false;
    const is_admin = JSON.parse(sessionStorage.getItem("is_admin")) || false;
    const esicEmployeeLimit = 21000;
    const salary_calculation = "gross";
    let isEditAccess = (is_super_admin || is_admin);
    const canEditSalary = canAccessEmployeeProfile('salary', 'edit');
    const viewingOwnSalary = isOwnEmployeeProfile(employeeID);
    const salaryPayslipLocked = viewingOwnSalary && !!customConfig.lock_employee_salary_payslip;

    // state
    const [formData, setFormData] = useState(null)
    const [currentEmployee, setCurrentEmployee] = useState(employeeData)
    const [initialFormData, setInitialFormData] = useState(init)
    const [dropdownData, setDropdownData] = useState({
        designation: [],
        department: [],
        empgroup: [],
        salTemp: []
    })
    const [selectedTemplateData, setSelectedTemplateData] = useState({})
    const [selectedTemplateId, setSelectedTemplateId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [groupedTemplates, setGroupedTemplates] = useState(new Map())

    // input states
    const [salaryType, setSalaryType] = useState(null);
    const [grossAmountMonthly, setGrossAmountMonthly] = useState("");
    const [grossAmountYearly, setGrossAmountYearly] = useState("");
    const [grossError, setGrossError] = useState("");
    const [templateMismatch, setTemplateMismatch] = useState(false);
    const skipAutoRecalcRef = useRef(true);
    const userAppliedTemplateRef = useRef(false);
    const allTemplatesRef = useRef([]);

    const [earnings, setEarnings] = useState([]);
    const [deductions, setDeductions] = useState([])

    // api calls
    const getEmployeeDepartment = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeDesignation = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeGroup = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_GROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getSalaryTemplate = useCallback(async () => {
        try {
            const apiData = await fetch(SALARY_TEMPLATE, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                const employeeDeptId = toLookupId(employeeData?.professional_info?.department);
                const employeeDesigId = toLookupId(employeeData?.professional_info?.designation);
                const allTemplates = Array.isArray(apiData.data) ? apiData.data : [];
                allTemplatesRef.current = allTemplates;

                const activeTemplates = allTemplates.filter((item) => item?.is_active !== false);
                const matching = activeTemplates.filter((item) =>
                    templateMatchesEmployee(item, employeeDeptId, employeeDesigId)
                );
                const templates = matching;

                const templateList = groupBy(templates, templateGroupLabel);

                setGroupedTemplates(templateList);
                return templates;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch, employeeData]);

    const fetchDropdown = async () => {
        setLoading(true)
        const [designation, department, empgroup, salTemp] = await Promise.all([
            getEmployeeDesignation(),
            getEmployeeDepartment(),
            getEmployeeGroup(),
            getSalaryTemplate()
        ])

        const dropdowns = {
            designation: designation || [],
            department: department || [],
            empgroup: empgroup || [],
            salTemp: salTemp || []
        }

        setDropdownData(dropdowns)
        setLoading(false)

        return dropdowns
    }

    const getEmployeeSalDetails = async (id) => {
        try {
            const response = await fetch(url, { id: id })
            if (response.status === "fail") {
                setFormData(null)
                return
            }
            const salaryRecord = response.data?.result?.[0] || (Array.isArray(response.data) ? response.data[0] : null)
            if (salaryRecord) {
                setFormData(salaryRecord)
            } else {
                setFormData(null)
            }
        } catch (error) {
            ToastMsg('error', error.message)
        }
    }

    useEffect(() => {
        if (canEditSalary) fetchDropdown();
    }, [employeeData?.professional_info?.department, employeeData?.professional_info?.designation, canEditSalary]);

    useEffect(() => {
        if (employeeID) {
            getEmployeeSalDetails(employeeID)
        }
    }, [employeeID])

    // Set initial form data from the saved snapshot. Do not recalc from the
    // latest template just because Salary Details was opened.
    useEffect(() => {
        if (!formData || Array.isArray(formData)) {
            return
        }
        skipAutoRecalcRef.current = true;
        userAppliedTemplateRef.current = false;
        const monthly = toNumberOrNull(formData?.gross_salary_monthly_amount);
        const yearly = toNumberOrNull(formData?.gross_salary_annual_amount);
        setGrossAmountMonthly(monthly == null ? "" : monthly);
        setGrossAmountYearly(yearly == null ? "" : yearly);
        setGrossError("");

        if (formData?.template) {
            setSelectedTemplateId(formData.template);
            const template = dropdownData.salTemp.find(temp => temp.id === formData.template)
                || allTemplatesRef.current.find(temp => temp.id === formData.template);
            if (template) {
                setSelectedTemplateData(template);
            }
        }

        if (Array.isArray(formData.earnings) && formData.earnings.length) {
            setEarnings([...formData.earnings]);
        }

        if (Array.isArray(formData.deductions) && formData.deductions.length) {
            setDeductions([...formData.deductions]);
        }

        setSalaryType(formData?.salary_type || null);

        setInitialFormData(prev => ({
            ...prev,
            is_active: formData.is_active,
            salary_id: formData.id,
            salary_type: formData?.salary_type || null,
        }))
    }, [formData, dropdownData.salTemp]);

    const getfilterEmployeeGroupDetails = (employee, employeeGroups) => {
        if (!employee?.professional_info?.employee_group || !Array.isArray(employeeGroups)) return null;
        const groupId = employee.professional_info.employee_group;

        // Find the group that matches the employee's group ID
        const groupDetails = employeeGroups.find(group => group.id === groupId);

        return groupDetails || null;
    };

    const employeeGroupInfo = getfilterEmployeeGroupDetails(currentEmployee, dropdownData.empgroup);

    const getfilterEmployeeSuitableTemplate = (employee, designation, department) => {
        if (!employee?.professional_info?.department ||
            !employee?.professional_info?.designation ||
            !Array.isArray(designation) ||
            !Array.isArray(department) ||
            !dropdownData.salTemp) {
            return null;
        }

        const desigId = toLookupId(employee.professional_info.designation);
        const deptId = toLookupId(employee.professional_info.department);

        // Find the salary template that matches both department and designation
        const suitableTemplate = dropdownData.salTemp.find((template) =>
            template?.is_active !== false && templateMatchesEmployee(template, deptId, desigId)
        );

        return suitableTemplate || null;
    };

    useEffect(() => {
        if (formData?.template || selectedTemplateId || (formData?.earnings || []).length) {
            return;
        }
        if (dropdownData.salTemp && dropdownData.designation && dropdownData.department) {
            const template = getfilterEmployeeSuitableTemplate(
                employeeData,
                dropdownData.designation,
                dropdownData.department
            );

            if (template) {
                handleTemplateChange(template.id);
            }
        }
    }, [dropdownData.salTemp, dropdownData.designation, dropdownData.department, employeeData, formData]);

    useEffect(() => {
        const assignedId = formData?.template || selectedTemplateId;
        if (!assignedId) {
            setTemplateMismatch(false);
            return;
        }
        const assigned = dropdownData.salTemp.find((temp) => temp.id === assignedId)
            || allTemplatesRef.current.find((temp) => temp.id === assignedId);
        const deptId = toLookupId(employeeData?.professional_info?.department);
        const desigId = toLookupId(employeeData?.professional_info?.designation);
        if (!assigned || deptId == null || desigId == null) {
            setTemplateMismatch(false);
            return;
        }
        setTemplateMismatch(!templateMatchesEmployee(assigned, deptId, desigId));
    }, [formData?.template, selectedTemplateId, dropdownData.salTemp, employeeData]);

    // Create options for Select component from grouped templates
    const templateOptions = Array.from(groupedTemplates.entries()).flatMap(([groupName, templates]) => {
        const groupOptions = templates.map(template => ({
            label: `${template.salary_template_name}`,
            value: template.id,
            group: groupName
        }));

        return groupOptions;
    });

    // Alternative: If you want to use OptGroup for better organization
    const templateOptionsWithGroups = Array.from(groupedTemplates.entries()).map(([groupName, templates]) => ({
        label: groupName,
        options: templates.map(template => ({
            label: template.salary_template_name,
            value: template.id,
        }))
    }));
    const selectedInOptions = templateOptionsWithGroups.some((group) =>
        (group.options || []).some((option) => option.value === selectedTemplateId)
    );
    if (selectedTemplateId && !selectedInOptions) {
        const assigned = allTemplatesRef.current.find((temp) => temp.id === selectedTemplateId);
        templateOptionsWithGroups.unshift({
            label: "Currently assigned",
            options: [{
                label: assigned?.salary_template_name || formData?.template_name || "Assigned template",
                value: selectedTemplateId,
            }],
        });
    }

    // onchange
    const handleTemplateChange = (templateId) => {
        skipAutoRecalcRef.current = false;
        userAppliedTemplateRef.current = true;
        setSelectedTemplateId(templateId);

        const template = dropdownData.salTemp.find(temp => temp.id === templateId)
            || allTemplatesRef.current.find(temp => temp.id === templateId);
        if (!template) {
            return;
        }
        setSelectedTemplateData(template);

        // Update earnings and deductions based on selected template, but preserve existing IDs and metadata
        if (template) {
            const mergedEarnings = (template.earning_components || []).map(templateEarning => {
                const existingEarning = earnings.find(e => e.name === templateEarning.name);
                return {
                    ...templateEarning,
                    // Preserve existing ID and metadata if it exists
                    ...(existingEarning && {
                        id: existingEarning.id,
                        created_by: existingEarning.created_by,
                        created_date: existingEarning.created_date,
                        modified_by: existingEarning.modified_by,
                        modified_date: existingEarning.modified_date,
                        is_draft: existingEarning.is_draft,
                        salary_id: existingEarning.salary_id,
                        is_active: existingEarning.is_active
                    })
                };
            });
            setEarnings(mergedEarnings);

            // For deductions: merge template data with existing data to preserve IDs
            const mergedDeductions = (template.deduction_components || []).map(templateDeduction => {
                const existingDeduction = deductions.find(d => d.name === templateDeduction.name);
                return {
                    ...templateDeduction,
                    // Preserve existing ID and metadata if it exists
                    ...(existingDeduction && {
                        id: existingDeduction.id,
                        created_by: existingDeduction.created_by,
                        created_date: existingDeduction.created_date,
                        modified_by: existingDeduction.modified_by,
                        modified_date: existingDeduction.modified_date,
                        is_draft: existingDeduction.is_draft,
                        salary_id: existingDeduction.salary_id,
                        is_active: existingDeduction.is_active
                    })
                };
            });
            setDeductions(mergedDeductions);
        }
    };

    const handleGrossAmountChange = (value, type) => {
        skipAutoRecalcRef.current = false;
        const parsed = parseOptionalNumber(value);
        if (parsed === "" || parsed === "-" || parsed === "." || parsed === "-.") {
            setGrossError("");
            if (type === "monthly") {
                setGrossAmountMonthly(parsed === "-" || parsed === "-." ? parsed : "");
                setGrossAmountYearly("");
            } else {
                setGrossAmountYearly(parsed === "-" || parsed === "-." ? parsed : "");
                setGrossAmountMonthly("");
            }
            return;
        }
        const numValue = Number(parsed);
        if (!Number.isFinite(numValue)) return;
        if (numValue < 0) {
            setGrossError("Monthly and Yearly Gross cannot be negative.");
            if (type === "monthly") setGrossAmountMonthly(value);
            else setGrossAmountYearly(value);
            return;
        }
        if (type === "monthly" && numValue > GROSS_MONTHLY_MAX) {
            setGrossError(`Monthly Gross cannot exceed ₹${GROSS_MONTHLY_MAX.toLocaleString("en-IN")}.`);
            setGrossAmountMonthly(GROSS_MONTHLY_MAX);
            setGrossAmountYearly(round2(GROSS_MONTHLY_MAX * 12));
            return;
        }
        if (type === "yearly" && numValue > GROSS_MONTHLY_MAX * 12) {
            setGrossError(`Yearly Gross cannot exceed ₹${(GROSS_MONTHLY_MAX * 12).toLocaleString("en-IN")}.`);
            setGrossAmountYearly(round2(GROSS_MONTHLY_MAX * 12));
            setGrossAmountMonthly(GROSS_MONTHLY_MAX);
            return;
        }
        setGrossError("");
        if (type === "monthly") {
            const monthly = clampGrossMonthly(numValue);
            setGrossAmountMonthly(monthly);
            setGrossAmountYearly(round2(monthly * 12));
        } else {
            const yearly = round2(numValue);
            setGrossAmountYearly(yearly);
            setGrossAmountMonthly(clampGrossMonthly(yearly / 12));
        }
    };


    const deductionsApply = customConfig?.is_deduction !== false && !(
        employeeGroupInfo?.is_contract_employee === true
        && customConfig?.deduct_for_contract_emp === false
    );

    useEffect(() => {
        if (skipAutoRecalcRef.current) {
            return;
        }
        const monthly = Number(grossAmountMonthly);
        if (!monthly || monthly <= 0) {
            setEarnings((prev) => prev.map((row) => ({
                ...row,
                monthly_amount: 0,
                annual_amount: 0,
            })));
            setDeductions((prev) => prev.map((row) => ({
                ...row,
                employee_share_monthly_amount: 0,
                employee_share_annual_amount: 0,
                employer_share_monthly_amount: 0,
                employer_share_annual_amount: 0,
                monthly_amount: 0,
                annual_amount: 0,
            })));
            return;
        }

        const earningSource = userAppliedTemplateRef.current && selectedTemplateData?.earning_components?.length
            ? selectedTemplateData.earning_components
            : earnings;
        const deductionSource = userAppliedTemplateRef.current && selectedTemplateData?.deduction_components?.length
            ? selectedTemplateData.deduction_components
            : deductions;

        const earningComps = (earningSource || []).map((row) => ({
            ...row,
            name: row.name,
            percentage: row.percentage,
            calcof: row.calcof || selectedTemplateData?.earning_components?.find((item) => item.name === row.name)?.calcof || "CTC",
        }));
        const deductionComps = deductionsApply
            ? (deductionSource || []).map((row) => ({
                ...row,
                name: row.name,
                calcof: row.calcof || selectedTemplateData?.deduction_components?.find((item) => item.name === row.name)?.calcof || "CTC",
                is_amount: row.is_amount,
                is_employee_share: row.is_employee_share,
                is_employer_share: row.is_employer_share,
                employee_share_percentage: row.employee_share_percentage,
                employer_share_percentage: row.employer_share_percentage,
                monthly_amount: row.monthly_amount ?? row.employee_share_monthly_amount,
                employee_share_monthly_amount: row.employee_share_monthly_amount ?? row.monthly_amount,
            }))
            : [];

        const breakdown = calculateSalaryBreakdown(
            monthly,
            earningComps,
            deductionComps,
            {
                pfLimit: customConfig.pf_limit || 15000,
                capAmount: customConfig.cap_amount || 0,
                esicLimitUpto: esicEmployeeLimit,
                salaryDeduction: customConfig.salary_deduction || "gross",
            },
        );

        setEarnings((prev) => {
            const existingMap = prev.reduce((acc, item) => {
                acc[item.name] = item;
                return acc;
            }, {});
            return (breakdown.earnings || []).map((row) => ({
                ...(existingMap[row.name] || {}),
                ...row,
                monthly_amount: round2(row.monthly_amount),
                annual_amount: round2(row.annual_amount),
            }));
        });

        if (!deductionsApply) {
            setDeductions([]);
            return;
        }

        setDeductions((prev) => {
            const existingMap = prev.reduce((acc, item) => {
                acc[item.name] = item;
                return acc;
            }, {});
            return (breakdown.deductions || []).map((row) => ({
                ...(existingMap[row.name] || {}),
                ...row,
                employee_share_annual_amount: round2((row.employee_share_monthly_amount || 0) * 12),
                employer_share_annual_amount: round2((row.employer_share_monthly_amount || 0) * 12),
            }));
        });
    }, [grossAmountMonthly, selectedTemplateData, deductionsApply]);

    useEffect(() => {
        const totalMonthlyEarnings = earnings.reduce((acc, curr) => {
            return acc + (Number(curr.monthly_amount) || 0);
        }, 0);

        const totalAnnualEarnings = totalMonthlyEarnings * 12;

        const totalEmployeeDeductionMonthly = deductions.reduce((acc, curr) => acc + (curr.employee_share_monthly_amount || 0), 0);
        const totalEmployerDeductionMonthly = deductions.reduce((acc, curr) => acc + (curr.employer_share_monthly_amount || 0), 0);
        const totalDeductionsMonthly = totalEmployeeDeductionMonthly + totalEmployerDeductionMonthly;
        const totalDeductionsAnnual = totalDeductionsMonthly * 12;

        const netMonthly = totalMonthlyEarnings - totalEmployeeDeductionMonthly;
        const netAnnual = netMonthly * 12;

        const totalMonthlyCTC = totalMonthlyEarnings + totalEmployerDeductionMonthly;
        const totalAnnualCTC = totalMonthlyCTC * 12;

        const totalDeductionPercentage = totalMonthlyEarnings
            ? (totalDeductionsMonthly / totalMonthlyEarnings) * 100
            : 0;

        const employeeDeductionPercent = totalMonthlyEarnings
            ? (totalEmployeeDeductionMonthly / totalMonthlyEarnings) * 100
            : 0;

        const finalPayload = {
            ...initialFormData,
            annual_ctc: totalAnnualCTC,
            monthly_ctc: totalMonthlyCTC,
            earnings: earnings,
            deductions: deductions,
            total_earning: {
                percentage: 100,
                monthly_amount: totalMonthlyEarnings,
                annual_amount: totalAnnualEarnings
            },
            total_deduction: {
                percentage: employeeDeductionPercent,
                employee_monthly_amount: totalEmployeeDeductionMonthly,
                employer_monthly_amount: totalEmployerDeductionMonthly,
                annual_amount: totalDeductionsAnnual
            },
            total_deductions_percentage: totalDeductionPercentage,
            total_deductions_monthly_amount: totalDeductionsMonthly,
            total_deductions_annual_amount: totalDeductionsAnnual,
            net_salary: netMonthly,
            net_salary_monthly: netMonthly,
            gross_salary: totalAnnualEarnings,
            gross_monthly_amount: totalMonthlyEarnings,
            gross_annual_amount: totalAnnualEarnings,
            salary_template_id: selectedTemplateData?.id || 0,
            // Both save branches spread initialFormData, so carrying it here reaches
            // the payload without touching either of them.
            salary_type: salaryType || null
        };

        setInitialFormData(finalPayload);
    }, [earnings, deductions, grossAmountMonthly, selectedTemplateData, salaryType]);

    // submit call
    useImperativeHandle(ref, () => ({
        handleSaveSalDetails: async () => {
            if (!canEditSalary) return true;
            if (salaryPayslipLocked) return true;
            const monthlyGross = Number(grossAmountMonthly);
            const yearlyGross = Number(grossAmountYearly);
            if (monthlyGross < 0 || yearlyGross < 0) {
                ToastMsg("error", "Monthly and Yearly Gross cannot be negative.");
                return false;
            }
            if (monthlyGross > GROSS_MONTHLY_MAX || yearlyGross > GROSS_MONTHLY_MAX * 12) {
                ToastMsg("error", "Monthly or Yearly Gross exceeds the allowed limit.");
                return false;
            }
            if (salary_calculation === "gross") {

                let updated = {
                    ...initialFormData,
                    annual_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount * 12 : initialFormData.annual_ctc,
                    monthly_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.monthly_ctc,
                    net_salary: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary,
                    net_salary_monthly: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary_monthly,
                    deductions: deductionsApply ? initialFormData.deductions : [],
                    total_deductions_monthly_amount: employeeGroupInfo?.is_contract_employee === true ? 0 : initialFormData.total_deductions_monthly_amount,
                    earnings: earnings,
                };

                let updateFormData = { ...updated }
                updateFormData["employee_id"] = employeeID;
                updateFormData["salary_id"] = formData?.id ?? formData?.salary_info?.salary_id ?? null;
                updateFormData["is_active"] = formData?.is_active ?? formData?.salary_info?.is_active ?? true;
                updateFormData["gross_salary_percentage"] = updateFormData?.total_earning?.percentage ? updateFormData?.total_earning?.percentage : 0;
                updateFormData["gross_salary_monthly_amount"] = Number(grossAmountMonthly) || Number(updateFormData.gross_monthly_amount) || 0;
                updateFormData["gross_salary_annual_amount"] = Number(grossAmountYearly) || Number(updateFormData.gross_annual_amount) || 0;
                updateFormData["template"] = selectedTemplateId || updateFormData?.salary_template_id || "";
                updateFormData["total_deductions_percentage"] = updateFormData?.total_deductions_percentage || updateFormData?.total_deduction?.percentage || 0;
                updateFormData["total_deductions_monthly_amount"] = updateFormData?.total_deductions_monthly_amount || 0;
                updateFormData["total_deductions_annual_amount"] = updateFormData?.total_deductions_annual_amount || updateFormData?.total_deduction?.annual_amount || 0;
                if (!Number(updateFormData.annual_ctc) && updateFormData.gross_salary_monthly_amount) {
                    const employerMonthly = (deductions || []).reduce((acc, curr) => acc + (Number(curr.employer_share_monthly_amount) || 0), 0);
                    updateFormData.monthly_ctc = updateFormData.gross_salary_monthly_amount + employerMonthly;
                    updateFormData.annual_ctc = updateFormData.monthly_ctc * 12;
                }
                delete updateFormData.total_earning;
                delete updateFormData.total_deduction;
                delete updateFormData.gross_salary;
                delete updateFormData.gross_monthly_amount;
                delete updateFormData.gross_annual_amount;
                delete updateFormData.salary_template_id;
                if (!updateFormData.effective_from) {
                    updateFormData.effective_from = new Date().toISOString().slice(0, 10);
                }

                try {
                    const response = await update(url, updateFormData, false)

                    if (response.status !== 'fail') {
                        console.log(response.message);
                        // ToastMsg('success', response.message)
                    } else {
                        ToastMsg('error', response.message)
                        return false
                    }
                } catch (error) {
                    ToastMsg('error', error.message)
                    return false
                }
                // Add your actual save logic here
            } else if (salary_calculation === "ctc") {
                console.log("Saving salary details with CTC calculation", initialFormData);
                // Add your actual save logic here
            }
        }
    }))

    // loader
    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    // table columns
    const earningColumns = [
        {
            title: "Description",
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{text}</span>,
            className: "bg-green-50 dark:bg-green-900/20"
        },
        {
            title: "Percentage",
            dataIndex: "percentage",
            key: "percentage",
            render: (text) => <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{text}%</span>,
            className: "bg-blue-50 dark:bg-blue-900/20",
            align: 'center'
        },
        {
            title: "Monthly Amount",
            dataIndex: "monthly_amount",
            key: "monthly_amount",
            render: (amount) => <span className="text-sm font-semibold text-green-600 dark:text-green-400">₹{(Number(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
            className: "bg-emerald-50 dark:bg-emerald-900/20",
            align: 'right'
        },
        {
            title: "Annual Amount",
            dataIndex: "annual_amount",
            key: "annual_amount",
            render: (amount) => <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">₹{(Number(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
            className: "bg-violet-50 dark:bg-violet-900/20",
            align: 'right'
        },
    ];

    const deductionColumns = [
        {
            title: "Description",
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{text}</span>,
            className: "bg-red-50 dark:bg-red-900/20"
        },
        {
            title: "Percentage",
            dataIndex: "employee_share_percentage",
            key: "percentage",
            render: (text) => <span className="text-sm font-medium text-orange-600 dark:text-orange-400">{text}%</span>,
            className: "bg-amber-50 dark:bg-amber-900/20",
            align: 'center'
        },
        {
            title: "Monthly Amount",
            dataIndex: "employee_share_monthly_amount",
            key: "employee_share_monthly_amount",
            render: (amount) => <span className="text-sm font-semibold text-red-600 dark:text-red-400">₹{(Number(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
            className: "bg-rose-50 dark:bg-rose-900/20",
            align: 'right'
        },
        {
            title: "Annual Amount",
            dataIndex: "employee_share_annual_amount",
            key: "employee_share_annual_amount",
            render: (amount) => <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">₹{(Number(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
            className: "bg-pink-50 dark:bg-pink-900/20",
            align: 'right'
        },
    ];

    // preview chart — earnings only, skip zero slices so centre total matches
    const chartSlices = (earnings || []).filter((row) => Number(row.monthly_amount) > 0);
    const chartTotal = chartSlices.reduce((sum, row) => sum + Number(row.monthly_amount || 0), 0);
    const hasData = chartSlices.length > 0;
    const contributionData = (deductionsApply ? deductions : []).reduce((acc, row) => {
        if (Number(row.employer_share_monthly_amount) > 0) {
            acc[row.name] = {
                employerAmount: Number(row.employer_share_monthly_amount) || 0,
                percentage: row.employer_share_percentage ?? 0,
            };
        }
        return acc;
    }, {});

    const chartData = {
        labels: hasData
            ? chartSlices.map((row) => (row.name || "").toUpperCase())
            : ["No Data"],

        datasets: [
            {
                label: "Earnings",
                data: hasData
                    ? chartSlices.map((row) => Number(row.monthly_amount) || 0)
                    : [0],

                backgroundColor: [
                    "#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40",
                    "#8BC34A", "#F44336", "#00BCD4", "#FFEB3B", "#E91E63", "#3F51B5"
                ],
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11
                    },
                    color: undefined,
                }
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${label}: ₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${percentage}%)`;
                    }
                }
            }
        }
    };

    // Calculate summary values
    const totalMonthlyEarnings = earnings.reduce((acc, curr) => acc + (Number(curr.monthly_amount) || 0), 0);
    const totalEmployeeDeduction = deductionsApply
        ? deductions.reduce((acc, curr) => acc + (Number(curr.employee_share_monthly_amount) || 0), 0)
        : 0;
    const totalEmployerContribution = deductionsApply
        ? deductions.reduce((acc, curr) => acc + (Number(curr.employer_share_monthly_amount) || 0), 0)
        : 0;

    const netPay = totalMonthlyEarnings - totalEmployeeDeduction;
    const monthlyCTCIncludingPF = totalMonthlyEarnings + totalEmployerContribution;
    const yearlyCTCIncludingPF = monthlyCTCIncludingPF * 12;
    const templateDisplayName =
        formData?.template_name
        || dropdownData.salTemp.find((temp) => temp.id === selectedTemplateId)?.salary_template_name
        || "";
    const salaryTypeDisplay =
        salaryTypeOptions.find((option) => option.value === salaryType)?.label
        || (salaryType ? String(salaryType) : "—");

    if (salaryPayslipLocked) {
        return (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6 text-center">
                <h2 className="text-md font-bold text-gray-900 dark:text-white">Salary Details</h2>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                    Salary and payslip details are locked while payroll is being processed. Please contact HR.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Salary Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete employee Salary break downs and deduction details
                    </p>
                </div>
                <div className="flex flex-col items-end gap-3">
                    {canEditSalary ? (
                    <div className="flex items-end gap-3">
                    <div className="space-y-2 flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            Salary Type :
                        </label>
                        <Select
                            size="large"
                            value={salaryType}
                            placeholder="Select Salary Type"
                            options={salaryTypeOptions}
                            onChange={(value) => setSalaryType(value || null)}
                            allowClear
                            className={cn(
                                "w-44 rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
                            )}
                        />
                    </div>
                    <div className="space-y-2 flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            Select Template :
                        </label>
                        <Select
                            showSearch
                            size="large"
                            value={selectedTemplateId}
                            placeholder="Select Template"
                            options={templateOptionsWithGroups}
                            onChange={handleTemplateChange}
                            filterOption={(input, option) => {
                                const label = option?.label || option?.children;
                                return label?.toLowerCase().includes(input.toLowerCase());
                            }}
                            className={cn(
                                "w-64 rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>
                    </div>
                    ) : (
                    <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Salary Type:</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{salaryTypeDisplay}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-600 dark:text-gray-400">Template:</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{templateDisplayName || "—"}</span>
                        </div>
                    </div>
                    )}
                </div>
            </div>

            {!canEditSalary && <EmployeeProfileViewOnlyBanner sectionLabel="salary details" />}

            {templateMismatch && (
                <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                    The assigned salary template no longer matches this employee&apos;s department or designation.
                    Re-apply a matching template to update the salary structure.
                </div>
            )}
            {grossError && (
                <div className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-200">
                    {grossError}
                </div>
            )}

            {/* earnings and deduction table section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* first card */}
                <div className="lg:col-span-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="p-4">
                        {/* amount fields */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Monthly Gross
                                </label>
                                <Input
                                    type="number"
                                    value={formatGrossInput(grossAmountMonthly)}
                                    onChange={(e) => handleGrossAmountChange(e.target.value, 'monthly')}
                                    placeholder="Enter Monthly gross"
                                    disabled={loading || !canEditSalary}
                                    readOnly={!canEditSalary}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Yearly Gross
                                </label>
                                <Input
                                    type="number"
                                    value={formatGrossInput(grossAmountYearly)}
                                    onChange={(e) => handleGrossAmountChange(e.target.value, 'yearly')}
                                    placeholder="Enter Yearly gross"
                                    disabled={loading || !canEditSalary}
                                    readOnly={!canEditSalary}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                        </div>

                        {/* table */}
                        <div className="flex flex-col gap-6">
                            {/* Earnings Table */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <TrendingUp className="w-5 h-5 text-green-500" />
                                            <label className="text-sm font-medium text-gray-900 dark:text-white">Earnings</label>
                                        </div>
                                        <span className="text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-1 rounded-md">
                                            {earnings?.length || 0} components
                                        </span>
                                    </div>
                                </div>
                                <div className="p-1">
                                    <Table
                                        columns={earningColumns}
                                        dataSource={earnings}
                                        pagination={false}
                                        size="middle"
                                        className="custom-table"
                                        rowKey="id"
                                        rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                                    />
                                </div>
                            </div>

                            {/* Deductions Table */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <TrendingDown className="w-5 h-5 text-red-500" />
                                            <label className="text-sm font-medium text-gray-900 dark:text-white">Deductions</label>
                                        </div>
                                        {deductionsApply && (
                                            <span className="text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-1 rounded-md">
                                                {deductions?.length || 0} components
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-1">
                                    {customConfig?.is_deduction === false ? (
                                        // Case 1: Global Deductions turned off 
                                        <div className="py-10 text-center">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                <svg className="mx-auto h-14 w-14 mb-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <p className="text-sm font-medium mb-1">Deductions Not Applicable</p>
                                                <p className="text-xs max-w-md mx-auto leading-relaxed">
                                                    Deductions are not applicable as per company policy. You will receive your full gross pay.
                                                </p>
                                            </div>
                                        </div>
                                    ) : employeeGroupInfo?.is_contract_employee === true &&
                                        customConfig?.deduct_for_contract_emp === false ? (
                                        // Case 2: Contract employee but no deductions
                                        <div className="py-10 text-center">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                <svg className="mx-auto h-14 w-14 mb-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-sm font-medium mb-1">Contract Employee Benefits</p>
                                                <p className="text-xs max-w-md mx-auto leading-relaxed">
                                                    Deductions are not applicable for contract employees as per company policy. You will receive your full gross pay.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        // Case 3: Deductions are applicable
                                        <>
                                            <Table
                                                columns={deductionColumns}
                                                dataSource={deductions}
                                                pagination={false}
                                                size="middle"
                                                className="custom-table"
                                                rowKey="id"
                                                rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Note */}
                        <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-3">
                                        Important Notes
                                    </h4>
                                    <ul className="space-y-1">
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">Net Salary</span> excludes employer contributions and represents take-home pay.
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">ESIC</span> will apply only if the monthly gross salary is <span className="font-semibold text-blue-900 dark:text-blue-100">₹{esicEmployeeLimit?.toLocaleString()}</span> or below.
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">PF</span> is calculated based on Basic salary up to <span className="font-semibold text-blue-900 dark:text-blue-100">₹{(customConfig?.pf_limit || 15000)?.toLocaleString()}</span> as per statutory limits.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* second card */}
                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="p-4">
                        <div className="flex flex-col space-y-6">
                            {/* Chart Container */}
                            <div className="flex justify-center">
                                <div className="relative" style={{ width: '280px', height: '280px' }}>
                                    <Doughnut data={chartData} options={chartOptions} />
                                    {/* Chart Center Info */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="text-center rounded-full bg-white/90 dark:bg-gray-800/90 px-4 py-3 shadow-sm">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                ₹{chartTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Salary Summary Cards - Enhanced Design */}
                            <div className="space-y-4">
                                {/* Net Pay - Highlighted */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-green-800 dark:text-green-200">Net Pay Salary</p>
                                                <p className="text-xs text-green-600 dark:text-green-400">Your take home amount</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-green-900 dark:text-green-100">
                                                ₹{netPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                            <p className="text-xs text-green-700 dark:text-green-300 mt-1">Monthly</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Two Column Grid for Other Metrics */}
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Total Deduction */}
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-red-800 dark:text-red-200">Deductions</p>
                                                </div>
                                                <p className="text-xs text-red-600 dark:text-red-400">From your gross</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-red-900 dark:text-red-100">
                                                ₹{totalEmployeeDeduction.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Monthly CTC */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Monthly CTC</p>
                                                </div>
                                                <p className="text-xs text-blue-600 dark:text-blue-400">With employer shares</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-blue-900 dark:text-blue-100">
                                                ₹{monthlyCTCIncludingPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Yearly CTC */}
                                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-purple-800 dark:text-purple-200">Yearly CTC</p>
                                                </div>
                                                <p className="text-xs text-purple-600 dark:text-purple-400">Annual cost to company</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-purple-900 dark:text-purple-100">
                                                ₹{yearlyCTCIncludingPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Gross Salary */}
                                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Gross Salary</p>
                                                </div>
                                                <p className="text-xs text-amber-600 dark:text-amber-400">Before deductions</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-amber-900 dark:text-amber-100">
                                                ₹{totalMonthlyEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Employer Contributions - Enhanced */}
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
                                            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-semibold text-amber-800 dark:text-amber-200">Employer Contributions</h3>
                                            <p className="text-xs text-amber-600 dark:text-amber-400">Additional benefits provided</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-md font-bold text-amber-900 dark:text-amber-100">
                                            ₹{totalEmployerContribution.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-xs text-amber-700 dark:text-amber-300">Total monthly</p>
                                    </div>
                                </div>

                                {Object.keys(contributionData || {}).length > 0 ? (
                                    <div className="space-y-3">
                                        {Object.keys(contributionData).map((key) => (
                                            <div key={key} className="flex items-center justify-between p-3 bg-white dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-700/50">
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 capitalize">
                                                            {key.replace(/_/g, ' ')}
                                                        </p>
                                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                                            {contributionData[key]?.percentage || 0}% of base
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="text-sm font-semibold text-amber-900 dark:text-amber-100 whitespace-nowrap ml-3">
                                                    ₹{(contributionData[key]?.employerAmount || 0).toLocaleString('en-IN', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6">
                                        <svg className="w-12 h-12 text-amber-300 dark:text-amber-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">No employer contributions</p>
                                        <p className="text-xs text-amber-500 dark:text-amber-500 mt-1">Configure deductions to see contributions</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
})

export default SalaryDetailsContent;